import { useState } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { testimonials } from '@/mocks/about';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honey = (formData.get('company_alt') as string || '').trim();
    if (honey) {
      setFormStatus('success');
      form.reset();
      return;
    }

    setFormStatus('loading');
    try {
      const res = await fetch('https://readdy.ai/api/form/d9qo9jknlsngrm4lm2r0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          name: formData.get('name') as string,
          email: formData.get('email') as string,
          subject: formData.get('subject') as string,
          message: formData.get('message') as string,
        }).toString(),
      });
      const text = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(text); } catch (_err) { /* ignore parse error */ }
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后再试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接');
      setFormStatus('error');
    }
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于福利导航',
            description: '福利导航是一站式福利资源聚合平台，致力于帮助用户发现和享受各类福利资源。',
            url: 'https://fulidaohang.com/about',
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: '福利导航',
            url: 'https://fulidaohang.com/',
            description: '一站式福利资源聚合平台，汇聚折扣优惠、会员权益、生活补贴、职场福利等信息。',
            contactPoint: {
              '@type': 'ContactPoint',
              contactType: 'customer service',
              email: 'contact@fulidaohang.com',
              availableLanguage: ['Chinese'],
            },
          }),
        }}
      />

      <main className="pt-20">
        <div className="w-full px-4 md:px-6 py-8 bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>
        </div>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-5">
                关于福利导航
              </h1>
              <p className="text-sm text-foreground-500 max-w-2xl mx-auto leading-relaxed mb-8">
                福利导航创立于2024年，是一站式福利资源聚合平台。我们致力于汇聚各类折扣优惠、会员权益、生活补贴、职场福利等信息，帮助每一位用户发现并享受属于自己的福利资源，让实惠触手可及。
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 max-w-2xl mx-auto">
                {[
                  { icon: 'ri-eye-line', num: '500万+', label: '累计访问量' },
                  { icon: 'ri-article-line', num: '200+', label: '福利文章' },
                  { icon: 'ri-user-smile-line', num: '50万+', label: '月活跃用户' },
                ].map((stat) => (
                  <div
                    key={stat.label}
                    className="p-6 rounded-lg bg-background-100 border border-background-200/70"
                  >
                    <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-50 text-primary-600 mb-3">
                      <i className={`${stat.icon} text-lg`}></i>
                    </div>
                    <div className="text-2xl font-bold text-foreground-900">{stat.num}</div>
                    <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  我们的使命
                </h2>
                <p className="text-sm text-foreground-500 max-w-xl mx-auto">
                  在信息爆炸的时代，找到真正有价值的福利信息并不容易。我们的使命是帮助用户省时省力地发现最适合自己的福利资源
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {[
                  {
                    icon: 'ri-shield-check-line',
                    title: '信息真实可靠',
                    desc: '每一条福利信息都经过编辑团队的人工核实，确保链接有效、规则准确、时效最新。对于政府补贴类信息，我们直接引用官方文件作为依据。',
                  },
                  {
                    icon: 'ri-time-line',
                    title: '更新及时高效',
                    desc: '折扣优惠在活动开始前1-3天发布，政府补贴政策在官方发布后24小时内更新，会员权益信息每月进行核查，确保您获取的信息始终新鲜。',
                  },
                  {
                    icon: 'ri-shake-hands-line',
                    title: '完全免费开放',
                    desc: '福利导航是公益性质的信息聚合平台，所有内容对用户完全免费开放，没有任何付费壁垒。我们相信好的福利信息应该被更多人知道。',
                  },
                  {
                    icon: 'ri-user-heart-line',
                    title: '用户需求导向',
                    desc: '我们持续收集用户反馈，不断优化信息分类和展示方式。您的每一次使用和反馈，都在帮助我们把福利导航变得更好。',
                  },
                ].map((item) => (
                  <div
                    key={item.title}
                    className="flex gap-4 p-5 rounded-lg bg-background-50 border border-background-200/70"
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-50 text-accent-600 shrink-0">
                      <i className={`${item.icon} text-lg`}></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">
                        {item.title}
                      </h3>
                      <p className="text-xs text-foreground-500 leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  用户口碑
                </h2>
                <p className="text-sm text-foreground-500">
                  听听用户们怎么说
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {testimonials.map((t) => (
                  <div
                    key={t.name}
                    className="p-5 rounded-lg bg-background-100 border border-background-200/70"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center shrink-0">
                        <span className="text-sm font-bold text-secondary-600">
                          {t.name.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-foreground-800">{t.name}</div>
                        <div className="text-xs text-foreground-400">{t.role}</div>
                      </div>
                    </div>
                    <p className="text-xs text-foreground-600 leading-relaxed">
                      "{t.content}"
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  联系我们
                </h2>
                <p className="text-sm text-foreground-500">
                  有任何问题、建议或合作意向，欢迎通过以下方式联系我们
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
                {[
                  { icon: 'ri-mail-line', label: '电子邮箱', value: 'contact@fulidaohang.com' },
                  { icon: 'ri-wechat-line', label: '微信公众号', value: '福利导航' },
                  { icon: 'ri-time-line', label: '工作时间', value: '周一至周五 9:00-18:00' },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="p-4 rounded-lg bg-background-50 border border-background-200/70 text-center"
                  >
                    <div className="w-9 h-9 mx-auto flex items-center justify-center rounded-full bg-primary-50 text-primary-600 mb-2">
                      <i className={`${item.icon} text-base`}></i>
                    </div>
                    <div className="text-xs text-foreground-500 mb-1">{item.label}</div>
                    <div className="text-sm font-medium text-foreground-800">{item.value}</div>
                  </div>
                ))}
              </div>

              {formStatus === 'success' ? (
                <div className="p-8 rounded-lg bg-primary-50 text-center">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
                    <i className="ri-check-line text-2xl"></i>
                  </div>
                  <h3 className="text-base font-semibold text-primary-700 mb-1">
                    发送成功！
                  </h3>
                  <p className="text-xs text-primary-600">
                    感谢您的留言，我们会在1-2个工作日内回复您。
                  </p>
                  <button
                    onClick={() => setFormStatus('idle')}
                    className="mt-4 px-5 py-2 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    继续留言
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form=""
                  className="flex flex-col gap-4"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input
                      type="text"
                      name="name"
                      placeholder="您的姓名"
                      required
                      className="px-4 py-3 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    />
                    <input
                      type="email"
                      name="email"
                      placeholder="您的邮箱"
                      required
                      className="px-4 py-3 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    />
                  </div>
                  <input
                    type="text"
                    name="subject"
                    placeholder="主题"
                    required
                    className="px-4 py-3 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                  />
                  <textarea
                    name="message"
                    placeholder="请输入您的留言..."
                    rows={5}
                    maxLength={500}
                    required
                    className="px-4 py-3 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                  ></textarea>
                  <input
                    type="text"
                    name="company_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="form-honey"
                  />
                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="px-6 py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors disabled:opacity-60 whitespace-nowrap cursor-pointer"
                  >
                    {formStatus === 'loading' ? '发送中...' : '发送留言'}
                  </button>
                  {formStatus === 'error' && (
                    <p className="text-xs text-accent-600 text-center">{formError}</p>
                  )}
                </form>
              )}
            </div>
          </div>
        </section>
      </main>
    </>
  );
}